import { Box, Theme } from "@mui/material";
import { ChartDataset, ScriptableContext } from "chart.js";
import "chart.js/auto";
import { useMemo } from "react";
import { Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../../context/CurrencyContext";
import { useTheme } from "../../context/ThemeContext";
import { percentage } from "../../shared/percentage";
import { Sample } from "../../shared/PortfolioTypes";
import { getTooltip } from "../../utils/chartjsTheming";
import { signedBar, toneColors } from "../general/dataAtoms/shared";
import {
  RISK_CHART_HEIGHT,
  RISK_LINE_POINTS,
  riskChartScales,
} from "./riskChart";

/** The width of the line of the curve, in px. */
const CURVE_WIDTH = 2.5;

/** A point of the curve: a value, and the probability of a result up to it. */
export interface CurvePoint {
  x: number;
  y: number;
  /** Where the curve passes zero: the two parts meet here, it is no result. */
  crossing?: true;
}

/**
 * A distribution of values as a cumulative curve, the worst value first, in
 * the two parts it is drawn in: the one below zero and the one from zero up.
 * Where the curve passes zero between two values, both parts reach to that
 * point, so that they meet on the line of zero.
 */
export function valueAtRiskCurve(data: Sample[]): {
  below: CurvePoint[];
  above: CurvePoint[];
} {
  const sorted = data
    .filter(({ probability }) => probability > 0)
    .sort((a, b) => a.value - b.value);
  const total = sorted.reduce((sum, { probability }) => sum + probability, 0);

  let cumulativeProbability = 0;
  const points: CurvePoint[] = sorted.map(({ value, probability }) => {
    cumulativeProbability += probability;
    return { x: cumulativeProbability / total, y: value };
  });

  const below = points.filter(({ y }) => y < 0);
  const above = points.filter(({ y }) => y >= 0);
  const last = below[below.length - 1];
  const [first] = above;
  if (last && first) {
    const crossing: CurvePoint = {
      x: last.x + ((first.x - last.x) * -last.y) / (first.y - last.y),
      y: 0,
      crossing: true,
    };
    below.push(crossing);
    // a part that starts at zero itself is on the line already
    if (first.y > 0) above.unshift(crossing);
  }
  return { below, above };
}

/**
 * How the two parts of the curve are drawn: adverse below zero, favourable
 * from zero up, each as a line over a see-through area.
 */
const curveColors = (theme: Theme) => {
  const tones = toneColors(theme);
  return {
    below: { fill: signedBar(theme, -1, true).bgcolor, line: tones.adverse },
    above: { fill: signedBar(theme, 1, true).bgcolor, line: tones.favourable },
  };
};

/** What the two tones of the curve stand for. */
export const ValueAtRiskLegend = () => {
  const { theme } = useTheme();
  const { t } = useTranslation(undefined, { keyPrefix: "varChart.legend" });
  const colors = curveColors(theme);

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        columnGap: 2.25,
        rowGap: 0.5,
        typography: "caption",
        fontWeight: 500,
        color: "text.secondary",
      }}
    >
      {(["below", "above"] as const).map((part) => (
        <Box
          key={part}
          sx={{ display: "flex", alignItems: "center", gap: 0.875 }}
        >
          <Box
            sx={{
              flex: "none",
              width: 11,
              height: 11,
              borderRadius: 0.75,
              bgcolor: colors[part].fill,
              border: `1px solid ${colors[part].line}`,
            }}
          />
          {t(part)}
        </Box>
      ))}
    </Box>
  );
};

/**
 * The value at risk of a distribution: its values against the probability of
 * a result up to each, the worst first.
 */
const CumulativeValueAtRiskChart = ({ data }: { data: Sample[] }) => {
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const { t } = useTranslation(undefined, { keyPrefix: "varChart" });

  const curve = useMemo(() => valueAtRiskCurve(data), [data]);
  const colors = curveColors(theme);

  const part = (
    key: "below" | "above",
  ): ChartDataset<"line", CurvePoint[]> => ({
    data: curve[key],
    // the area lies between the curve and the line of zero
    fill: "origin",
    backgroundColor: colors[key].fill,
    borderColor: colors[key].line,
    borderWidth: CURVE_WIDTH,
    ...RISK_LINE_POINTS,
    // the pointer reads the values of the distribution, not the point the
    // two parts meet in
    pointHitRadius: ({ raw }: ScriptableContext<"line">) =>
      (raw as CurvePoint | undefined)?.crossing
        ? 0
        : RISK_LINE_POINTS.pointHitRadius,
  });

  return (
    <Box sx={{ height: RISK_CHART_HEIGHT }}>
      <Line
        data={{ datasets: [part("below"), part("above")] }}
        options={{
          // the chart fills the frame it is given rather than keeping a shape
          maintainAspectRatio: false,
          interaction: {
            mode: "nearest",
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              ...getTooltip(theme),
              callbacks: {
                title: () => "",
                label: (item) => {
                  const value = item.parsed.y ?? 0;
                  if (value < 0) {
                    return t("negativeValue", {
                      probability: percentage.format(item.parsed.x ?? 0),
                      currency: currency.format(value),
                    });
                  }
                  return t("positiveValue", {
                    probability: percentage.format(1 - (item.parsed.x ?? 0)),
                    currency: currency.format(value),
                  });
                },
              },
              itemSort: (a, b) => b.datasetIndex - a.datasetIndex,
            },
          },
          scales: riskChartScales({
            theme,
            currency,
            axisTitle: t("axis"),
            // zero stays in view: the two parts of the curve stand on its line
            y: { beginAtZero: true },
          }),
        }}
      />
    </Box>
  );
};

export default CumulativeValueAtRiskChart;
